import * as Yup from 'yup';
import { Category } from 'src/@types/Category';

// Path (category) form ⇄ API body (categories-order-reset C1, C5, X3).
//
// The form used to `reset()` itself with the whole list row and PATCH it
// back, and had no order field: every path stayed at order 1, a stored order
// of 0 or > 1000 made every edit a 400, and the update DTO filled the keys a
// body left out (seo_*, active, slug) from initialisers. The API now `$set`s
// only the keys it receives, so this module sends exactly the form's own
// fields — never `_id`, `active`, `slug`, `seo_*`, `tracks`, `createdAt`,
// `duration` or `points` from the row. Mirrors the API's validators
// (UpdateCategoryDto) so a 400 is rare; one on `order` still goes under the
// field (orderServerError).

export const ORDER_MIN = 0;
export const ORDER_MAX = 1000;
export const ORDER_RANGE_TEXT = 'Display order must be a whole number from 0 to 1000';
export const KEYWORD_MIN_TEXT = 'Each keyword needs at least 3 characters';

export type CategoryFormValues = {
  title: string;
  title_ar: string;
  description: string;
  description_ar: string;
  icon: string;
  image: string;
  keywords: string[];
  keywords_ar: string[];
  // Text, so a stored 0 shows as "0" (RHFTextField blanks a numeric 0);
  // '' while empty (create: the API puts the path last). The resolver hands
  // the submit handler Yup's cast value (a number or undefined).
  order: string | number | undefined;
  showOnHome: boolean;
};

export type CategoryBody = {
  title: string;
  title_ar?: string;
  description: string;
  description_ar?: string;
  icon: string;
  image: string;
  keywords: string[];
  keywords_ar?: string[];
  order?: number;
  // Create: always. Edit: only when the staffer changed the switch (below).
  showOnHome?: boolean;
};

const strings = (list: unknown): string[] =>
  Array.isArray(list) ? list.filter((k) => typeof k === 'string') : [];

/** The form's values for a stored path (or an empty create form). */
export function categoryFormValues(category?: Category): CategoryFormValues {
  return {
    title: category?.title || '',
    title_ar: category?.title_ar || '',
    description: category?.description || '',
    description_ar: category?.description_ar || '',
    icon: category?.icon || '',
    image: category?.image || '',
    keywords: strings(category?.keywords),
    keywords_ar: strings(category?.keywords_ar),
    order: typeof category?.order === 'number' ? String(category.order) : '',
    // Absent = shown (the API stores no default).
    showOnHome: category?.showOnHome !== false,
  };
}

const emptyToUndefined = (v: unknown, original: unknown) =>
  original === '' || original === null || original === undefined ? undefined : v;

/** Client rules = the API's (UpdateCategoryDto / CreateCategoryDto). */
export function categorySchema(isEdit: boolean) {
  const order = Yup.number()
    .transform(emptyToUndefined)
    .typeError(ORDER_RANGE_TEXT)
    .integer(ORDER_RANGE_TEXT)
    .min(ORDER_MIN, ORDER_RANGE_TEXT)
    .max(ORDER_MAX, ORDER_RANGE_TEXT);
  return Yup.object().shape({
    title: Yup.string()
      .trim()
      .min(3, 'Path title needs at least 3 characters')
      .max(128, 'Path title must be 128 characters or less')
      .required('Path title is required'),
    // The create API requires it (3-128); an edit may leave it empty (not sent).
    title_ar: Yup.string()
      .trim()
      .test('ar-title', 'Arabic title needs 3 to 128 characters', (v) => {
        const len = (v || '').length;
        return (len === 0 && isEdit) || (len >= 3 && len <= 128);
      }),
    icon: Yup.string().required('Path Icon is required'),
    image: Yup.string().required('Path Image is required'),
    description: Yup.string()
      .trim()
      .min(3, 'Description needs at least 3 characters')
      .required('Description is required'),
    description_ar: Yup.string(),
    keywords: Yup.array()
      .of(Yup.string().min(3, KEYWORD_MIN_TEXT))
      .min(1, 'Add at least one keyword')
      .required('Add at least one keyword'),
    keywords_ar: Yup.array().of(Yup.string().min(3, KEYWORD_MIN_TEXT)),
    // Edit: a stored path always has an order, so the field must hold one.
    order: isEdit ? order.required(ORDER_RANGE_TEXT) : order,
    showOnHome: Yup.boolean(),
  });
}

/** '' / null / not a number → undefined; otherwise the number. */
export function parseOrder(value: unknown): number | undefined {
  if (value === '' || value === null || value === undefined) return undefined;
  const n = typeof value === 'number' ? value : Number(String(value).trim());
  return Number.isFinite(n) ? n : undefined;
}

/**
 * The POST / PATCH body: only the form's own fields. An empty Arabic title
 * is left out (the API wants 3+ characters when one is sent); on create the
 * empty Arabic description / keywords are left out too, on edit they are
 * sent so clearing them is saved. No order on create = the API puts the
 * path last.
 *
 * The home picker switch (R5 late safety addition, plans/R5.md §3.2 item 2):
 * on create it is always sent; on edit ONLY when the staffer changed it from
 * the loaded path (`loaded`). A path with no stored switch loads as ON; if an
 * untouched ON were sent, any title edit before scripts/backfill-category-order.js
 * ran would store `true` on the path the home page hides, and the backfill
 * (which never overwrites a stored switch) would then show it on the home page.
 */
export function categoryPayload(
  values: CategoryFormValues,
  isEdit: boolean,
  loaded?: Category
): CategoryBody {
  const body: CategoryBody = {
    title: (values.title || '').trim(),
    description: (values.description || '').trim(),
    icon: values.icon || '',
    image: values.image || '',
    keywords: strings(values.keywords),
  };
  const show = values.showOnHome !== false;
  if (!isEdit || show !== categoryFormValues(loaded).showOnHome) body.showOnHome = show;
  const titleAr = (values.title_ar || '').trim();
  if (titleAr) body.title_ar = titleAr;
  const descriptionAr = (values.description_ar || '').trim();
  if (descriptionAr || isEdit) body.description_ar = descriptionAr;
  const keywordsAr = strings(values.keywords_ar);
  if (keywordsAr.length || isEdit) body.keywords_ar = keywordsAr;
  const order = parseOrder(values.order);
  if (order !== undefined) body.order = order;
  return body;
}

/**
 * The API's 400 messages about `order` (class-validator writes them as
 * "order must not be greater than 1000"), for the field's helper text.
 * '' when the error is about something else.
 */
export function orderServerError(err: any): string {
  const list: unknown[] = Array.isArray(err?.messages)
    ? err.messages
    : Array.isArray(err?.message)
    ? err.message
    : [];
  const hits = list.map((m) => (typeof m === 'string' ? m : '')).filter((m) => /^order\b/.test(m));
  return hits.length ? `${ORDER_RANGE_TEXT} (${hits.join('; ')})` : '';
}

/** A typed keyword added to a list: trimmed, empty and duplicates ignored. */
export function addKeyword(list: string[] | undefined, raw: unknown): string[] {
  const current = strings(list);
  const word = typeof raw === 'string' ? raw.trim() : '';
  if (!word || current.includes(word)) return current;
  return [...current, word];
}

/** The list without the keyword at `index` (a copy; the form state is never mutated). */
export function removeKeywordAt(list: string[] | undefined, index: number): string[] {
  return strings(list).filter((_k, i) => i !== index);
}

/**
 * The message of a react-hook-form error for a list field: the list's own
 * ("Add at least one keyword") or the first item's ("Each keyword needs …").
 */
export function firstErrorMessage(error: unknown): string {
  if (!error || typeof error !== 'object') return '';
  const own = (error as any).message;
  if (typeof own === 'string' && own) return own;
  const items = Object.values(error as Record<string, any>);
  const hit = items.find(
    (e) => e && typeof e === 'object' && typeof e.message === 'string' && e.message
  );
  return hit ? (hit as any).message : '';
}
